// src/screens/LockSessionHireView.jsx

import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  Alert,
  Box,
  Button,
  Center,
  Container,
  Flex,
  Loader,
  Text,
  Title,
} from '@mantine/core';

import {
  CardElement,
  Elements,
  PaymentRequestButtonElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';

import {
  loadStripe,
} from '@stripe/stripe-js';

import {
  FaChevronLeft,
  FaClock,
  FaCreditCard,
  FaDollarSign,
} from 'react-icons/fa';

import {
  TimerWheelView,
} from 'react-timer-wheel-kit';

import {
  DotNetAPI,
} from '../atoms/dotnetAPI';

const APP_CLIP_ACCENT = '#228be6';

const UI = {
  screen: {
    position: 'relative',
    minHeight: '100dvh',
    overflow: 'hidden',
    background:
      'linear-gradient(135deg, #ffffff 0%, #f1f3f5 48%, #ffffff 100%)',
  },

  content: {
    position: 'relative',
    zIndex: 2,
    width: '100%',
  },

  shell: {
    width: '100%',
    maxWidth: 500,
    marginInline: 'auto',
  },

  glassCard: {
    width: '100%',
    padding: 22,
    borderRadius: 30,
    background: 'rgba(255, 255, 255, 0.72)',
    border: '1px solid rgba(255, 255, 255, 0.56)',
    boxShadow: '0 22px 54px rgba(15, 23, 42, 0.08)',
    backdropFilter: 'blur(28px) saturate(150%)',
    WebkitBackdropFilter: 'blur(28px) saturate(150%)',
  },

  innerCard: {
    width: '100%',
    padding: 18,
    borderRadius: 20,
    background: 'rgba(255, 255, 255, 0.62)',
    border: '1px solid rgba(255, 255, 255, 0.54)',
    boxShadow: '0 8px 24px rgba(15, 23, 42, 0.045)',
    backdropFilter: 'blur(18px) saturate(145%)',
    WebkitBackdropFilter: 'blur(18px) saturate(145%)',
  },

  backButton: {
    width: 48,
    height: 48,
    minWidth: 48,
    padding: 0,
    borderRadius: '50%',
    color: '#1f2937',
    background: 'rgba(255, 255, 255, 0.70)',
    border: '1px solid rgba(255, 255, 255, 0.58)',
    boxShadow: '0 10px 28px rgba(15, 23, 42, 0.07)',
    backdropFilter: 'blur(20px) saturate(145%)',
    WebkitBackdropFilter: 'blur(20px) saturate(145%)',
  },

  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    minHeight: 42,
    padding: '9px 14px',
    borderRadius: 999,
    color: '#667085',
    background: 'rgba(255, 255, 255, 0.68)',
    border: '1px solid rgba(255, 255, 255, 0.56)',
    backdropFilter: 'blur(18px) saturate(145%)',
    WebkitBackdropFilter: 'blur(18px) saturate(145%)',
  },

  primaryButton: {
    height: 58,
    borderRadius: 18,
    fontWeight: 700,
    fontSize: 16,
    boxShadow: 'none',
  },

  cardElement: {
    minHeight: 58,
    display: 'flex',
    alignItems: 'center',
    padding: '17px 18px',
    borderRadius: 20,
    background: 'rgba(255, 255, 255, 0.76)',
    border: '1px solid rgba(255, 255, 255, 0.68)',
    boxShadow: '0 8px 24px rgba(15, 23, 42, 0.05)',
  },
};

function AmbientBackground() {
  return (
    <Box
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: 0,
      }}
    >
      <style>{`
        @keyframes slb-hire-orb-one {
          from { transform: translate3d(-30px, -120px, 0); }
          to { transform: translate3d(-115px, -245px, 0); }
        }

        @keyframes slb-hire-orb-two {
          from { transform: translate3d(50px, 100px, 0); }
          to { transform: translate3d(135px, 225px, 0); }
        }

        @keyframes slb-hire-orb-three {
          from { transform: translate3d(30px, -190px, 0); }
          to { transform: translate3d(110px, -100px, 0); }
        }

        .slb-wallet-button {
          border-radius: 18px;
          overflow: hidden;
        }

        .slb-card-element .StripeElement {
          width: 100%;
        }

        @media (prefers-reduced-motion: reduce) {
          .slb-hire-orb {
            animation: none !important;
          }
        }
      `}</style>

      <Box
        className="slb-hire-orb"
        style={{
          position: 'absolute',
          width: 340,
          height: 340,
          left: '7%',
          top: '6%',
          borderRadius: '50%',
          background: 'rgba(34, 139, 230, 0.18)',
          filter: 'blur(70px)',
          animation:
            'slb-hire-orb-one 8s ease-in-out infinite alternate',
        }}
      />

      <Box
        className="slb-hire-orb"
        style={{
          position: 'absolute',
          width: 320,
          height: 320,
          right: '3%',
          bottom: '2%',
          borderRadius: '50%',
          background: 'rgba(59, 130, 246, 0.12)',
          filter: 'blur(70px)',
          animation:
            'slb-hire-orb-two 10s ease-in-out infinite alternate',
        }}
      />

      <Box
        className="slb-hire-orb"
        style={{
          position: 'absolute',
          width: 230,
          height: 230,
          right: '18%',
          top: '10%',
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.78)',
          filter: 'blur(55px)',
          animation:
            'slb-hire-orb-three 7s ease-in-out infinite alternate',
        }}
      />
    </Box>
  );
}

function truthy(value) {
  return (
    value === true ||
    value === 1 ||
    value === '1' ||
    value === 'true'
  );
}

function extractPk(data) {
  if (
    !data ||
    typeof data !== 'object'
  ) {
    return null;
  }

  const tryList = obj => [
    obj?.stripe_key,
    obj?.primaryKey,
    obj?.stripe_pk,
    obj?.stripePublishableKey,
    obj?.stripe_publishable_key,
    obj?.publishable_key,
    obj?.publishableKey,
    obj?.pk,
    obj?.key,
  ].filter(Boolean);

  const flat =
    tryList(data);

  const business =
    data.business ||
    data.biz ||
    data.company ||
    {};

  const nestedBusiness =
    tryList(business);

  const nestedItem =
    tryList(
      data.item ||
      {}
    );

  return [
    ...flat,
    ...nestedBusiness,
    ...nestedItem,
  ].find(
    value =>
      typeof value === 'string' &&
      /^pk_(test|live)_/.test(value)
  ) || null;
}

function toNumber(
  value,
  fallback = 0
) {
  const numberValue =
    Number(value);

  return Number.isFinite(numberValue)
    ? numberValue
    : fallback;
}

function formatCurrency(value) {
  return new Intl.NumberFormat(
    'en-AU',
    {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  ).format(
    Number(value) || 0
  );
}

function getApiMessage(
  data,
  fallback
) {
  return (
    data?.message ||
    data?.error ||
    fallback
  );
}

const cardElementOptions = {
  hidePostalCode: true,

  style: {
    base: {
      color: '#1f2937',
      fontSize: '17px',
      fontWeight: '500',
      lineHeight: '24px',
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',

      '::placeholder': {
        color: '#98a2b3',
      },
    },

    invalid: {
      color: '#e03131',
      iconColor: '#e03131',
    },
  },
};

export default function LockSessionHireView(
  props
) {
  const {
    stripePublishableKey,
  } = props;

  const [
    stripePromise,
    setStripePromise
  ] =
    useState(null);

  const [
    loading,
    setLoading
  ] =
    useState(true);

  const [
    bootError,
    setBootError
  ] =
    useState('');

  useEffect(() => {
    let alive = true;

    async function initialiseStripe() {
      try {
        const pk =
          typeof stripePublishableKey === 'string'
            ? stripePublishableKey
            : extractPk(
                stripePublishableKey
              );

        if (
          !pk ||
          !/^pk_(test|live)_/.test(pk)
        ) {
          throw new Error(
            'Stripe publishable key not provided for this business.'
          );
        }

        const promise =
          loadStripe(pk);

        if (alive) {
          setStripePromise(promise);
        }
      } catch (error) {
        if (alive) {
          setBootError(
            error?.message ||
            'Failed to initialise payments.'
          );
        }
      } finally {
        if (alive) {
          setLoading(false);
        }
      }
    }

    initialiseStripe();

    return () => {
      alive = false;
    };
  }, [
    stripePublishableKey
  ]);

  if (bootError) {
    return (
      <Box style={UI.screen}>
        <AmbientBackground />

        <Container
          size={560}
          px="md"
          style={UI.content}
        >
          <Center
            style={{
              minHeight: '100dvh',
            }}
          >
            <Alert
              color="red"
              title="Payment unavailable"
              radius={20}
              style={{
                ...UI.glassCard,
                maxWidth: 500,
              }}
            >
              {bootError}
            </Alert>
          </Center>
        </Container>
      </Box>
    );
  }

  if (
    loading ||
    !stripePromise
  ) {
    return (
      <Box style={UI.screen}>
        <AmbientBackground />

        <Center
          style={{
            position: 'relative',
            zIndex: 2,
            minHeight: '100dvh',
          }}
        >
          <Loader
            size="md"
            color={APP_CLIP_ACCENT}
          />
        </Center>
      </Box>
    );
  }

  return (
    <Elements
      stripe={stripePromise}
    >
      <LockSessionHireViewInner
        {...props}
      />
    </Elements>
  );
}

function LockSessionHireViewInner({
  itemID,
  item,
  phoneNumber,
  onPaymentSuccess,
  onCancel,
}) {
  const stripe =
    useStripe();

  const elements =
    useElements();

  const chargePerSession =
    truthy(
      item?.charge_per_session
    );

  const costBase =
    toNumber(
      item?.hire_cost_base,
      0
    );

  const maxMinutes =
    toNumber(
      item?.hire_max_length,
      0
    );

  const itemTitle =
    item?.title ||
    'Session';

  const fixedSessionMinutes =
    maxMinutes;

  const fixedSessionCost =
    costBase;

  const fixedSessionTotalCents =
    Math.round(
      fixedSessionCost *
      100
    );

  const fixedSessionReady =
    chargePerSession &&
    fixedSessionMinutes > 0 &&
    fixedSessionTotalCents > 0;

  const [
    walletAvailable,
    setWalletAvailable
  ] =
    useState(false);

  const [
    paymentRequest,
    setPaymentRequest
  ] =
    useState(null);

  const [
    walletType,
    setWalletType
  ] =
    useState(null);

  const [
    paying,
    setPaying
  ] =
    useState(false);

  const [
    error,
    setError
  ] =
    useState('');

  const [
    cardComplete,
    setCardComplete
  ] =
    useState(false);

  const [
    hasSelection,
    setHasSelection
  ] =
    useState(
      fixedSessionReady
    );

  const [
    selectedMinutes,
    setSelectedMinutes
  ] =
    useState(
      fixedSessionReady
        ? fixedSessionMinutes
        : 0
    );

  const [
    selectedCost,
    setSelectedCost
  ] =
    useState(
      fixedSessionReady
        ? fixedSessionCost
        : 0
    );

  const selectedMinutesRef =
    useRef(
      fixedSessionReady
        ? fixedSessionMinutes
        : 0
    );

  const totalCentsRef =
    useRef(
      fixedSessionReady
        ? fixedSessionTotalCents
        : 0
    );

  const wheelDoneRef =
    useRef(null);

  useEffect(() => {
    if (!chargePerSession) {
      return;
    }

    const ready =
      fixedSessionMinutes > 0 &&
      fixedSessionTotalCents > 0;

    selectedMinutesRef.current =
      ready
        ? fixedSessionMinutes
        : 0;

    totalCentsRef.current =
      ready
        ? fixedSessionTotalCents
        : 0;

    setSelectedMinutes(
      ready
        ? fixedSessionMinutes
        : 0
    );

    setSelectedCost(
      ready
        ? fixedSessionCost
        : 0
    );

    setHasSelection(ready);
  }, [
    chargePerSession,
    fixedSessionMinutes,
    fixedSessionCost,
    fixedSessionTotalCents,
  ]);

  const createPaymentIntent =
    useCallback(
      async (
        minutes,
        totalCents
      ) => {
        if (!itemID) {
          throw new Error(
            'Item ID is missing.'
          );
        }

        if (!phoneNumber) {
          throw new Error(
            'Phone number is missing.'
          );
        }

        if (minutes <= 0) {
          throw new Error(
            'Invalid hire length.'
          );
        }

        if (totalCents <= 0) {
          throw new Error(
            'Invalid payment amount.'
          );
        }

        const response =
          await DotNetAPI.post(
            `hireable-items/${itemID}/payment`,
            {
              phone: phoneNumber,
              total: totalCents,
              minutes,
            }
          );

        const json =
          response?.data ||
          {};

        if (
          json?.status !== true
        ) {
          throw new Error(
            getApiMessage(
              json,
              'Failed to create payment.'
            )
          );
        }

        const data =
          json?.data ||
          {};

        const intent =
          data?.intent?.data ||
          {};

        const clientSecret =
          intent?.client_secret ||
          intent?.clientSecret ||
          data?.client_secret ||
          data?.clientSecret ||
          '';

        const transactionId =
          data?.transaction_id ||
          data?.transactionId ||
          null;

        if (!clientSecret) {
          console.error(
            'Payment intent response:',
            json
          );

          throw new Error(
            'Payment client secret was not returned.'
          );
        }

        if (!transactionId) {
          throw new Error(
            'Transaction ID was not returned.'
          );
        }

        return {
          clientSecret,
          transactionId,
        };
      },
      [
        itemID,
        phoneNumber,
      ]
    );

  const createSession =
    useCallback(
      async (
        transactionId,
        minutes
      ) => {
        const response =
          await DotNetAPI.post(
            `hireable-items/${itemID}/transactions/${transactionId}/session`,
            {
              phone: phoneNumber,
              minutes,
            }
          );

        const json =
          response?.data ||
          {};

        if (
          json?.status !== true
        ) {
          throw new Error(
            getApiMessage(
              json,
              'Payment succeeded but the session could not be created.'
            )
          );
        }

        const data =
          json?.data ||
          {};

        return {
          sessionId:
            data?.session_id,

          length:
            data?.length ||
            minutes,
        };
      },
      [
        itemID,
        phoneNumber,
      ]
    );

  const completePayment =
    useCallback(
      async (
        transactionId,
        minutes,
        totalCents
      ) => {
        const session =
          await createSession(
            transactionId,
            minutes
          );

        wheelDoneRef.current?.(
          true
        );

        await onPaymentSuccess?.({
          transactionId,

          sessionId:
            session.sessionId,

          length:
            session.length,

          minutes:
            session.length,

          totalCents,
        });
      },
      [
        createSession,
        onPaymentSuccess,
      ]
    );

  const handlePaymentMethod =
    useCallback(
      async event => {
        setError('');

        try {
          const minutes =
            selectedMinutesRef.current;

          const totalCents =
            totalCentsRef.current;

          if (
            minutes <= 0 ||
            totalCents <= 0
          ) {
            event.complete('fail');

            setError(
              'Please select your hire length before paying.'
            );

            return;
          }

          const {
            clientSecret,
            transactionId,
          } =
            await createPaymentIntent(
              minutes,
              totalCents
            );

          const {
            error: confirmError,
            paymentIntent,
          } =
            await stripe.confirmCardPayment(
              clientSecret,
              {
                payment_method:
                  event.paymentMethod.id,
              },
              {
                handleActions: false,
              }
            );

          if (confirmError) {
            event.complete('fail');

            wheelDoneRef.current?.(
              false
            );

            setError(
              confirmError?.message ||
              'Payment failed.'
            );

            return;
          }

          event.complete('success');

          if (
            paymentIntent?.status ===
            'requires_action'
          ) {
            const {
              error: actionError,
            } =
              await stripe.confirmCardPayment(
                clientSecret
              );

            if (actionError) {
              wheelDoneRef.current?.(
                false
              );

              setError(
                actionError?.message ||
                'Payment authorisation failed.'
              );

              return;
            }
          }

          await completePayment(
            transactionId,
            minutes,
            totalCents
          );
        } catch (paymentError) {
          try {
            event.complete('fail');
          } catch {
            // ignore
          }

          wheelDoneRef.current?.(
            false
          );

          setError(
            paymentError?.response?.data?.message ||
            paymentError?.message ||
            'Payment failed.'
          );
        }
      },
      [
        stripe,
        createPaymentIntent,
        completePayment,
      ]
    );

  const handlePaymentCancel =
    useCallback(
      () => {
        wheelDoneRef.current?.(
          false
        );
      },
      []
    );

  useEffect(() => {
    if (!stripe) {
      return undefined;
    }

    if (
      typeof window === 'undefined' ||
      !window.isSecureContext
    ) {
      return undefined;
    }

    const request =
      stripe.paymentRequest({
        country: 'AU',
        currency: 'aud',

        total: {
          label: itemTitle,
          amount:
            fixedSessionReady
              ? fixedSessionTotalCents
              : 100,
        },

        requestPayerName: false,
        requestPayerEmail: false,
        requestShipping: false,
      });

    let mounted = true;

    request
      .canMakePayment()
      .then(
        canMakePayment => {
          if (!mounted) {
            return;
          }

          const apple =
            Boolean(
              canMakePayment?.applePay
            );

          const google =
            Boolean(
              canMakePayment?.googlePay
            );

          if (
            !apple &&
            !google
          ) {
            setWalletAvailable(false);
            setPaymentRequest(null);
            setWalletType(null);
            return;
          }

          setWalletAvailable(true);
          setPaymentRequest(request);
          setWalletType(
            apple
              ? 'apple'
              : 'google'
          );

          request.on(
            'paymentmethod',
            handlePaymentMethod
          );

          request.on(
            'cancel',
            handlePaymentCancel
          );
        }
      )
      .catch(() => {
        if (!mounted) {
          return;
        }

        setWalletAvailable(false);
        setPaymentRequest(null);
        setWalletType(null);
      });

    return () => {
      mounted = false;

      try {
        request.off?.(
          'paymentmethod',
          handlePaymentMethod
        );
      } catch {
        // ignore
      }

      try {
        request.off?.(
          'cancel',
          handlePaymentCancel
        );
      } catch {
        // ignore
      }
    };
  }, [
    stripe,
    itemTitle,
    fixedSessionReady,
    fixedSessionTotalCents,
    handlePaymentMethod,
    handlePaymentCancel,
  ]);

  const preparePayment =
    (
      minutes,
      totalCents,
      done = null
    ) => {
      setError('');

      wheelDoneRef.current =
        done;

      selectedMinutesRef.current =
        minutes;

      totalCentsRef.current =
        totalCents;

      setHasSelection(true);

      if (
        paymentRequest &&
        walletAvailable
      ) {
        paymentRequest.update({
          total: {
            label:
              chargePerSession
                ? itemTitle
                : `Hire for ${minutes} min`,

            amount: totalCents,
          },
        });

        return;
      }

      // The timer wheel can stop its waiting state while the user
      // enters card details. We still retain the callback so a
      // successful card payment can complete the wheel flow.
      done?.(false);
    };

  const handleWheelPayment =
    (
      minutes,
      cost,
      done
    ) => {
      const numericCost =
        Number(cost) ||
        0;

      const cents =
        Math.round(
          numericCost *
          100
        );

      setSelectedMinutes(minutes);
      setSelectedCost(numericCost);

      preparePayment(
        minutes,
        cents,
        done
      );
    };

  const payWithCard =
    async () => {
      if (
        !stripe ||
        !elements ||
        paying
      ) {
        return;
      }

      if (!hasSelection) {
        setError(
          'Please select your hire length first.'
        );

        return;
      }

      if (!cardComplete) {
        setError(
          'Please complete your card details.'
        );

        return;
      }

      setPaying(true);
      setError('');

      try {
        const minutes =
          selectedMinutesRef.current;

        const totalCents =
          totalCentsRef.current;

        const {
          clientSecret,
          transactionId,
        } =
          await createPaymentIntent(
            minutes,
            totalCents
          );

        const card =
          elements.getElement(
            CardElement
          );

        if (!card) {
          throw new Error(
            'Card input not ready.'
          );
        }

        const {
          error: confirmError,
        } =
          await stripe.confirmCardPayment(
            clientSecret,
            {
              payment_method: {
                card,
              },
            }
          );

        if (confirmError) {
          throw confirmError;
        }

        await completePayment(
          transactionId,
          minutes,
          totalCents
        );
      } catch (paymentError) {
        wheelDoneRef.current?.(
          false
        );

        setError(
          paymentError?.response?.data?.message ||
          paymentError?.message ||
          'Card payment failed.'
        );
      } finally {
        setPaying(false);
      }
    };

  const displayMinutes =
    chargePerSession
      ? fixedSessionMinutes
      : selectedMinutes;

  const displayCost =
    chargePerSession
      ? fixedSessionCost
      : selectedCost;

  const paymentButtonLabel =
    hasSelection
      ? `Pay ${formatCurrency(displayCost)} & Continue`
      : 'Select hire length first';

  return (
    <Box
      key="lock-session-hire"
      style={UI.screen}
    >
      <AmbientBackground />

      <Container
        size={560}
        px="md"
        style={UI.content}
      >
        <Box
          style={{
            ...UI.shell,
            paddingTop: 20,
            paddingBottom: 100,
          }}
        >
          {onCancel && (
            <Button
              variant="subtle"
              aria-label="Back"
              onClick={() => {
                setError('');

                wheelDoneRef.current?.(
                  false
                );

                onCancel();
              }}
              style={UI.backButton}
            >
              <FaChevronLeft
                size={16}
              />
            </Button>
          )}

          <Flex
            direction="column"
            align="center"
            mt={onCancel ? 14 : 36}
          >
            <Title
              order={1}
              ta="center"
              style={{
                fontSize: 'clamp(30px, 7vw, 36px)',
                lineHeight: 1.08,
                fontWeight: 800,
                letterSpacing: '-0.035em',
                color: '#171717',
              }}
            >
              Start your hire
            </Title>

            <Text
              c="dimmed"
              ta="center"
              mt={8}
              style={{
                fontSize: 15,
                fontWeight: 500,
              }}
            >
              {itemTitle}
            </Text>

            {chargePerSession ? (
              <Flex
                justify="center"
                gap={10}
                wrap="wrap"
                mt={20}
              >
                <Box style={UI.pill}>
                  <FaDollarSign
                    size={14}
                    color={APP_CLIP_ACCENT}
                  />

                  <Text
                    size="sm"
                    fw={600}
                  >
                    {formatCurrency(
                      fixedSessionCost
                    )}
                  </Text>
                </Box>

                <Box style={UI.pill}>
                  <FaClock
                    size={14}
                    color={APP_CLIP_ACCENT}
                  />

                  <Text
                    size="sm"
                    fw={600}
                  >
                    {fixedSessionMinutes}{' '}
                    {fixedSessionMinutes === 1
                      ? 'minute'
                      : 'minutes'}
                  </Text>
                </Box>
              </Flex>
            ) : (
              <Text
                ta="center"
                mt={18}
                c="dimmed"
                style={{
                  maxWidth: 360,
                  lineHeight: 1.5,
                }}
              >
                Spin the wheel to choose how long you need access for.
              </Text>
            )}
          </Flex>

          <Box
            mt={24}
            style={UI.glassCard}
          >
            {!chargePerSession && (
              <>
                <Center>
                  <Box
                    style={{
                      width: '100%',
                      maxWidth: 400,
                    }}
                  >
                    <TimerWheelView
                      config={{
                        costPerMinute:
                          costBase,

                        maxMinutes,

                        allowsNegative:
                          false,

                        snapDegree:
                          30,

                        ringLineWidth:
                          46,

                        ringGradient: {
                          colors: [
                            'rgba(34,139,230,0.30)',
                            'rgba(34,139,230,0.90)',
                          ],
                        },

                        shouldPlaySuccessHaptic:
                          true,
                      }}
                      onRequestPayment={
                        handleWheelPayment
                      }
                    />
                  </Box>
                </Center>

                {selectedMinutes > 0 && (
                  <Box
                    mt={18}
                    style={UI.innerCard}
                  >
                    <Flex
                      justify="space-between"
                      align="center"
                      gap="md"
                    >
                      <Box>
                        <Text
                          size="xs"
                          c="dimmed"
                          fw={600}
                          tt="uppercase"
                          style={{
                            letterSpacing: '0.06em',
                          }}
                        >
                          Hire length
                        </Text>

                        <Text
                          fw={700}
                          mt={2}
                        >
                          {selectedMinutes}{' '}
                          {selectedMinutes === 1
                            ? 'minute'
                            : 'minutes'}
                        </Text>
                      </Box>

                      <Text
                        fw={800}
                        style={{
                          fontSize: 24,
                          letterSpacing: '-0.03em',
                        }}
                      >
                        {formatCurrency(
                          selectedCost
                        )}
                      </Text>
                    </Flex>
                  </Box>
                )}
              </>
            )}

            <Box
              mt={
                chargePerSession
                  ? 0
                  : 24
              }
            >
              <Text
                fw={700}
                style={{
                  fontSize: 18,
                }}
              >
                Payment
              </Text>

              <Text
                size="sm"
                c="dimmed"
                mt={3}
              >
                Use a supported wallet or enter your card details below.
              </Text>

              {hasSelection &&
                walletAvailable &&
                paymentRequest && (
                  <Box mt={18}>
                    <Text
                      size="xs"
                      c="dimmed"
                      fw={700}
                      tt="uppercase"
                      mb={8}
                      style={{
                        letterSpacing: '0.06em',
                      }}
                    >
                      {walletType === 'apple'
                        ? 'Apple Pay'
                        : 'Google Pay'}
                    </Text>

                    <Box
                      className="slb-wallet-button"
                      style={{
                        width: '100%',
                      }}
                    >
                      <PaymentRequestButtonElement
                        options={{
                          paymentRequest,

                          style: {
                            paymentRequestButton: {
                              type: 'buy',
                              theme: 'dark',
                              height: '54px',
                            },
                          },
                        }}
                      />
                    </Box>

                    <Flex
                      align="center"
                      gap={12}
                      my={18}
                    >
                      <Box
                        style={{
                          height: 1,
                          flex: 1,
                          background:
                            'rgba(15, 23, 42, 0.09)',
                        }}
                      />

                      <Text
                        size="xs"
                        c="dimmed"
                        fw={600}
                      >
                        OR PAY WITH CARD
                      </Text>

                      <Box
                        style={{
                          height: 1,
                          flex: 1,
                          background:
                            'rgba(15, 23, 42, 0.09)',
                        }}
                      />
                    </Flex>
                  </Box>
                )}

              <Box
                mt={
                  hasSelection &&
                  walletAvailable &&
                  paymentRequest
                    ? 0
                    : 18
                }
              >
                <Flex
                  align="center"
                  gap={8}
                  mb={8}
                >
                  <FaCreditCard
                    size={14}
                    color={APP_CLIP_ACCENT}
                  />

                  <Text
                    size="sm"
                    fw={700}
                  >
                    Card details
                  </Text>
                </Flex>

                <Box
                  className="slb-card-element"
                  style={UI.cardElement}
                >
                  <CardElement
                    options={
                      cardElementOptions
                    }
                    onChange={event => {
                      setCardComplete(
                        event.complete
                      );

                      if (event.error) {
                        setError(
                          event.error.message ||
                          'Please check your card details.'
                        );
                      } else if (
                        error &&
                        event.complete
                      ) {
                        setError('');
                      }
                    }}
                  />
                </Box>

                {!hasSelection &&
                  !chargePerSession && (
                    <Text
                      size="xs"
                      c="dimmed"
                      mt={8}
                    >
                      Select your hire length above to enable payment.
                    </Text>
                  )}

                <Button
                  fullWidth
                  mt={16}
                  color={APP_CLIP_ACCENT}
                  loading={paying}
                  disabled={
                    !hasSelection ||
                    !cardComplete ||
                    paying
                  }
                  onClick={
                    payWithCard
                  }
                  style={
                    UI.primaryButton
                  }
                >
                  {paymentButtonLabel}
                </Button>
              </Box>
            </Box>
          </Box>

          

          {error && (
            <Alert
              mt={16}
              color="red"
              variant="light"
              radius={18}
              style={{
                width: '100%',
                background:
                  'rgba(255,255,255,0.72)',
                border:
                  '1px solid rgba(224,49,49,0.14)',
                backdropFilter:
                  'blur(18px)',
                WebkitBackdropFilter:
                  'blur(18px)',
              }}
            >
              {error}
            </Alert>
          )}
        </Box>
      </Container>
    </Box>
  );
}
